import { isBooleanTrue, isListNotEmpty, isNotNullOrUndefined, isNumberGreaterThanOrEqualToZero, isNumberGreaterThanZero, isObjectNotEmpty, isStringEmpty, isStringNotEmpty } from "../utils/objectUtils";
import { convertToSlug } from "../utils/stringFormatter";
import { create, getAll, getById, getByIdQuery, getBySingleQuery, remove, update, updateField } from '../utils/dbUtils';
import { collection, endBefore, getCountFromServer, getDocs, limit, limitToLast, orderBy, query, startAfter, where } from 'firebase/firestore';
import { db, loadStorage } from '../firebase';
import { asTimeZone } from '../utils/dateFormatter';
import moment from "moment-timezone";
import { v4 as uuid } from 'uuid';
import { toLower } from "lodash";

class Campaign {
    static collectionName = 'campaigns';

    name = null;
    slug = convertToSlug(this.name);
    programId = null;
    productId = null;
    active = false;
    affiliateIds = [];

    color = null;
    bgColor = null;
    pageBgColor = null;
    cardBodyBgColor = null;
    cardBodyTextColor = null;
    cardBodyHeaderColor = null;
    title = null;
    subtitle = null;
    footer = null; // can contain html

    #image = null;
    imageRefId = null;

    quantity = 1;
    displayPrice = 0.0; // total price for the full quantity
    displayPriceCaveat = null; // can contain html

    introQuestion = null; // can contain html
    introPositiveActionText = null;
    introNegativeActionText = null;
    introNegativeActionResult = null; // can contain html

    detailSections = []; // array of ProductMetaData

    progressBarLabel = null;

    displayPayerCodes = []; // list of payerCodes to include in dropdown for insurance coverage
    payerCodes = []; // list of payerCodes to trigger conversion
    states = []; // list of states to trigger conversion
    cashPayOnly = false;
    cashPayOptional = false;

    constructor(campaign) {
        if (isObjectNotEmpty(campaign)) {
            if (isStringNotEmpty(campaign.name)) { this.name = campaign.name; }
            if (isStringNotEmpty(campaign.slug)) { this.slug = campaign.slug; }
            else if (isStringNotEmpty(campaign.name)) { this.slug = convertToSlug(campaign.name); }
            if (isStringNotEmpty(campaign.programId)) { this.programId = campaign.programId; }
            if (isStringNotEmpty(campaign.productId)) { this.productId = campaign.productId; }
            if (isBooleanTrue(campaign.active)) { this.active = true; }
            if (isListNotEmpty(campaign.affiliateIds)) { this.affiliateIds = campaign.affiliateIds; }

            if (isStringNotEmpty(campaign.color)) { this.color = campaign.color; }
            if (isStringNotEmpty(campaign.bgColor)) { this.bgColor = campaign.bgColor; }
            if (isStringNotEmpty(campaign.pageBgColor)) { this.pageBgColor = campaign.pageBgColor; }
            if (isStringNotEmpty(campaign.cardBodyBgColor)) { this.cardBodyBgColor = campaign.cardBodyBgColor; }
            if (isStringNotEmpty(campaign.cardBodyTextColor)) { this.cardBodyTextColor = campaign.cardBodyTextColor; }
            if (isStringNotEmpty(campaign.cardBodyHeaderColor)) { this.cardBodyHeaderColor = campaign.cardBodyHeaderColor; }
            if (isStringNotEmpty(campaign.title)) { this.title = campaign.title; }
            if (isStringNotEmpty(campaign.subtitle)) { this.subtitle = campaign.subtitle; }
            if (isNotNullOrUndefined(campaign.footer)) { this.footer = campaign.footer; }

            if (isNotNullOrUndefined(campaign.image)) { this.#image = campaign.image; }
            if (isStringNotEmpty(campaign.imageRefId)) { this.imageRefId = campaign.imageRefId; }

            if (isNumberGreaterThanZero(campaign.quantity)) { this.quantity = campaign.quantity; }
            if (isNumberGreaterThanOrEqualToZero(campaign.displayPrice)) { this.displayPrice = campaign.displayPrice; }
            if (isNotNullOrUndefined(campaign.displayPriceCaveat)) { this.displayPriceCaveat = campaign.displayPriceCaveat; }


            if (isNotNullOrUndefined(campaign.introQuestion)) { this.introQuestion = campaign.introQuestion; }
            if (isStringNotEmpty(campaign.introPositiveActionText)) { this.introPositiveActionText = campaign.introPositiveActionText; }
            if (isStringNotEmpty(campaign.introNegativeActionText)) { this.introNegativeActionText = campaign.introNegativeActionText; }
            if (isNotNullOrUndefined(campaign.introNegativeActionResult)) { this.introNegativeActionResult = campaign.introNegativeActionResult; }

            if (isListNotEmpty(campaign.detailSections)) { this.detailSections = campaign.detailSections; }

            if (isStringNotEmpty(campaign.progressBarLabel)) { this.progressBarLabel = campaign.progressBarLabel; }

            if (isListNotEmpty(campaign.displayPayerCodes)) { this.displayPayerCodes = campaign.displayPayerCodes; }
            if (isListNotEmpty(campaign.payerCodes)) { this.payerCodes = campaign.payerCodes; }
            if (isListNotEmpty(campaign.states)) { this.states = campaign.states; }
            if (isBooleanTrue(campaign.cashPayOnly)) { this.cashPayOnly = true; }
            if (isBooleanTrue(campaign.cashPayOptional)) { this.cashPayOptional = true; }
        }
    }

    toDto = () => {
        return {
            name: this.name,
            slug: this.slug,
            programId: this.programId,
            productId: this.productId,
            active: this.active,
            affiliateIds: this.affiliateIds,

            color: this.color,
            bgColor: this.bgColor,
            pageBgColor: this.pageBgColor,
            cardBodyBgColor: this.cardBodyBgColor,
            cardBodyTextColor: this.cardBodyTextColor,
            cardBodyHeaderColor: this.cardBodyHeaderColor,
            title: this.title,
            subtitle: this.subtitle,
            footer: this.footer,

            imageRefId: this.imageRefId,

            quantity: this.quantity,
            displayPrice: this.displayPrice,
            displayPriceCaveat: this.displayPriceCaveat,

            introQuestion: this.introQuestion,
            introPositiveActionText: this.introPositiveActionText,
            introNegativeActionText: this.introNegativeActionText,
            introNegativeActionResult: this.introNegativeActionResult,

            detailSections: this.detailSections,

            progressBarLabel: this.progressBarLabel,

            displayPayerCodes: this.displayPayerCodes,
            payerCodes: this.payerCodes,
            states: this.states,
            cashPayOnly: this.cashPayOnly,
            cashPayOptional: this.cashPayOptional,
        };
    }

    isValid = () => {
        if (isStringEmpty(this.name)) {
            throw new Error('Campaign name is required.');
        }

        return true;
    }

    static addFile = async (file) => {
        if (isNotNullOrUndefined(file)) {
            try {
                const fileName = uuid() + '.' + this.getFileExt(file);
                const { storage, ref, uploadBytes } = await loadStorage();
                const storageRef = ref(storage, 'campaigns/' + fileName);

                return await uploadBytes(storageRef, file);
            } catch (err) {
                console.log(err);
            }
        }

        return null;
    }

    static getFileExt = (file) => {
        const lastDot = file.name.lastIndexOf('.');
        //const fileName = fullFileName.substring(0, lastDot);
        const ext = file.name.substring(lastDot + 1);


        return toLower(ext);
    }

    static uploadImage = async (file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Campaign.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                return uploadRes.ref.fullPath;
            }
        }

        return null;
    }

    create = async () => {
        if (this.isValid()) {
            this.imageRefId = await Campaign.uploadImage(this.#image);

            return await create(Campaign.collectionName, this.toDto());
        }
    }

    static updateField = async (id, fieldName, newValue) => {
        if (isStringNotEmpty(id)) {
            return await updateField(Campaign.collectionName, id, fieldName, newValue);
        } else {
            throw new Error("id is required.");
        }
    }

    static update = async (id, updatedCampaign, oldCampaign) => {
        const updatedObj = new Campaign(updatedCampaign);
        if (updatedObj.isValid() && id === updatedCampaign.id) {
            // update the image if a new image is provided
            if (isNotNullOrUndefined(updatedObj.#image)) {
                updatedObj.imageRefId = await Campaign.uploadImage(updatedObj.#image);
            }

            const success = await update(Campaign.collectionName, id, updatedObj.toDto());
            if (isBooleanTrue(success)) {

                return { ...oldCampaign, ...updatedObj };
            }
        } else {
            throw new Error('id and id field of the updated campaign must match.')
        }

        return null;
    }

    static updateImage = async (id, file) => {
        if (isNotNullOrUndefined(file)) {
            const uploadRes = await Campaign.addFile(file);
            // console.log(uploadRes);
            if (isObjectNotEmpty(uploadRes)) {
                let imageRefId = uploadRes.ref.fullPath;
                let success = await Campaign.updateField(id, 'imageRefId', imageRefId);
                if (isBooleanTrue(success)) {
                    return imageRefId;
                }
            }
        }

        return null;
    }

    static delete = async (id) => {
        if (isStringNotEmpty(id)) {
            return await remove(Campaign.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getById = async (id) => {
        if (isStringNotEmpty(id)) {
            return await getById(Campaign.collectionName, id);
        } else {
            throw new Error("id is required.");
        }
    }

    static getAll = async () => {
        return await getAll(Campaign.collectionName);
    }

    static getByField = async (fieldName, fieldValue, comparer = '==') => {
        return await getBySingleQuery(Campaign.collectionName, fieldName, fieldValue, comparer);
    }

    static getByIds = async (ids = []) => {
        return await getByIdQuery(Campaign.collectionName, ids, 'in');
    }

    static getCount = async (queryParameters) => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = query(collection(db, Campaign.collectionName), ...queryConstraints);

        const snapShot = await getCountFromServer(q);

        //console.log(snapShot.data());

        return snapShot.data().count;
    }

    static getPaginated = async (queryParameters, pageSize = 25, firstVisible, lastVisible, orderByField = 'createdAt') => {
        const { start, end } = queryParameters;

        let queryConstraints = [];

        if (isNotNullOrUndefined(start)) {
            let startDate = asTimeZone(moment(new Date(start)), 'US/Pacific').startOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '>=', startDate));
        }

        if (isNotNullOrUndefined(end)) {
            let endDate = asTimeZone(moment(new Date(end)), 'US/Pacific').endOf('day').utc().toDate();
            queryConstraints.push(where('createdAt', '<=', endDate));
        }

        let q = null;
        if (isObjectNotEmpty(firstVisible)) {
            q = query(collection(db, Campaign.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limitToLast(pageSize), endBefore(firstVisible[orderByField], firstVisible['id']));
        } else if (isObjectNotEmpty(lastVisible)) {
            q = query(collection(db, Campaign.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize), startAfter(lastVisible[orderByField], lastVisible['id']));
        } else {
            q = query(collection(db, Campaign.collectionName), ...queryConstraints, orderBy(orderByField, 'asc'), orderBy('__name__', 'asc'), limit(pageSize));
        }

        let snapShot = await getDocs(q);
        return snapShot.docs.map((doc) => {
            return {
                id: doc.id,
                ...doc.data()
            };
        });
    }

    static get = async (queryParameters) => {
        let allRecords = [];
        try {
            let next = null;
            let totalPages = 0;

            let totalCount = await this.getCount(queryParameters);
            totalPages = Math.ceil(totalCount / 10000.0);
            // console.log('totalCount: ' + totalCount);
            // console.log('totalPages: ' + totalPages);
            let pages = [];
            for (let i = 0; i < totalPages; i++) {
                pages.push(i + 1);
            }
            for (const page of pages) {
                // console.log('starting page ' + page);

                let list = await this.getPaginated(queryParameters, 10000, null, next, 'createdAt');

                // console.log(list.length);
                allRecords = [...allRecords, ...list];

                // console.log(allRecords.length);

                // console.log('go to next page');
                next = list[list.length - 1];
            }
        } catch (error) {
            console.log(error);
        }

        return allRecords;
    }
};

export default Campaign;